import { useTranslation } from 'react-i18next';
import type { OperatorSlaBreakdown } from '@uniticket/shared';

interface SLAChartProps {
  onTrack: number;
  atRisk: number;
  breached: number;
  resolved: number;
  totalTickets: number;
  showOperatorBreakdown?: boolean;
  operatorBreakdown?: OperatorSlaBreakdown[];
}

const CIRCUMFERENCE = 2 * Math.PI * 50; // r=50

export function SLAChart({ onTrack, atRisk, breached, resolved, totalTickets, showOperatorBreakdown, operatorBreakdown }: SLAChartProps) {
  const { t } = useTranslation('dashboard');
  const total = onTrack + atRisk + breached + resolved;

  // Handle empty state
  if (total === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="gradient-band px-6 py-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">{t('slaChart.title')}</h3>
        </div>
        <div className="p-6 flex flex-col items-center justify-center min-h-[300px]">
          <div className="relative w-40 h-40">
            <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f5f9" strokeWidth="12" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-slate-300">0</span>
              <span className="text-xs text-slate-400">{t('slaChart.totalTickets')}</span>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-4">{t('slaChart.noData')}</p>
          <p className="text-xs text-slate-300 mt-1">{t('slaChart.noDataHint')}</p>
        </div>
      </div>
    );
  }

  const greenLen = (onTrack / total) * CIRCUMFERENCE;
  const yellowLen = (atRisk / total) * CIRCUMFERENCE;
  const redLen = (breached / total) * CIRCUMFERENCE;
  const slateLen = (resolved / total) * CIRCUMFERENCE;

  const greenPct = Math.round((onTrack / total) * 100);
  const yellowPct = Math.round((atRisk / total) * 100);
  const redPct = Math.round((breached / total) * 100);
  const slatePct = Math.round((resolved / total) * 100);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="gradient-band px-6 py-3">
        <h3 className="text-sm font-bold text-white uppercase tracking-wide">{t('slaChart.title')}</h3>
      </div>
      <div className="p-6 space-y-6">
        {/* Donut chart */}
        <div className="flex justify-center">
          <div className="relative w-40 h-40">
            <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f5f9" strokeWidth="12" />
              {/* Green: on track */}
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke="#22c55e" strokeWidth="12"
                strokeDasharray={`${greenLen} ${CIRCUMFERENCE}`}
                strokeDashoffset="0"
              />
              {/* Yellow: at risk */}
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke="#eab308" strokeWidth="12"
                strokeDasharray={`${yellowLen} ${CIRCUMFERENCE}`}
                strokeDashoffset={`${-greenLen}`}
              />
              {/* Red: breached */}
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke="#dc2626" strokeWidth="12"
                strokeDasharray={`${redLen} ${CIRCUMFERENCE}`}
                strokeDashoffset={`${-(greenLen + yellowLen)}`}
              />
              {/* Slate: resolved/closed */}
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke="#94a3b8" strokeWidth="12"
                strokeDasharray={`${slateLen} ${CIRCUMFERENCE}`}
                strokeDashoffset={`${-(greenLen + yellowLen + redLen)}`}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-slate-800">{totalTickets}</span>
              <span className="text-xs text-slate-400">{t('slaChart.totalTickets')}</span>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm text-slate-600">{t('slaChart.onTrack')}</span>
            </div>
            <span className="text-sm font-bold text-slate-800">
              {onTrack} <span className="text-xs font-normal text-slate-400">({greenPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-sm text-slate-600">{t('slaChart.atRisk')}</span>
            </div>
            <span className="text-sm font-bold text-slate-800">
              {atRisk} <span className="text-xs font-normal text-slate-400">({yellowPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm text-slate-600">{t('slaChart.breached')}</span>
            </div>
            <span className="text-sm font-bold text-slate-800">
              {breached} <span className="text-xs font-normal text-slate-400">({redPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-400" />
              <span className="text-sm text-slate-600">{t('slaChart.resolved')}</span>
            </div>
            <span className="text-sm font-bold text-slate-800">
              {resolved} <span className="text-xs font-normal text-slate-400">({slatePct}%)</span>
            </span>
          </div>
        </div>

        {/* Operator breakdown */}
        {showOperatorBreakdown && operatorBreakdown && operatorBreakdown.length > 0 && (
          <div className="pt-4 border-t border-slate-100">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('slaChart.byOperator')}</p>
            <div className="space-y-2.5">
              {operatorBreakdown.map((op) => (
                <div key={op.operatorName}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 font-medium">{op.operatorName}</span>
                    <span className="text-slate-400">{t('slaChart.tickets', { count: op.ticketCount })}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full flex overflow-hidden">
                    <div className="bg-green-500 rounded-l-full" style={{ width: `${op.onTrackPct}%` }} />
                    <div className="bg-yellow-500" style={{ width: `${op.atRiskPct}%` }} />
                    <div className="bg-red-500 rounded-r-full" style={{ width: `${op.breachedPct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
